import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_URL, System, SystemDetails } from '@frontend-site/shared/data';
import { catchError, forkJoin, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { SystemStore } from './system.store';
import { SessionRepository } from '../session';
import { map } from 'rxjs/operators';

const PQM = '/pqm';

@Injectable({ providedIn: 'root' })
export class SystemService {
    constructor(
        @Inject(API_URL) private readonly apiUrl: string,
        private readonly http: HttpClient,
        private readonly store: SystemStore,
        private readonly session: SessionRepository
    ) {}

    loadSystems(): Observable<System[]> {
        this.store.setLoading(true);
        return this.getSystems().pipe(
            tap((systems) => {
                this.store.setSystems(systems.filter((system) => system.companyId === this.session?.applicationConfig?.companyId));
                this.store.setLoading(false);
            }),
            catchError((err) => {
                this.store.setLoading(false);
                return throwError(() => err);
            })
        );
    }

    isUatOnly(systemId: number): boolean {
        return this.session.applicationConfig?.uatAccounts?.indexOf(systemId) !== -1;
    }

    loadSystemsDetails(recentItemsCount: number = -1): void {
        this.store.systems$
            .pipe(
                map((systems: System[]) => (recentItemsCount > -1 ? systems.slice(0, recentItemsCount) : systems)),
                switchMap((recentSystemsFromCurrentCompany: System[]) =>
                    forkJoin(recentSystemsFromCurrentCompany.map(({ id }) => this.getSystemDetails(id)))
                ),
                tap((systemsDetails) => this.store.update({ systemsDetails })),
                take(1)
            )
            .subscribe();
    }

    loadSystemDetails(systemId: number): Observable<SystemDetails> {
        this.store.update({ selectedSystemId: systemId });
        this.store.updateSystemDetails(systemId, null);
        return this.getSystemDetails(systemId).pipe(
            tap((systemDetails) => {
                this.store.updateSystemDetails(systemId, systemDetails);
            }),
            catchError((err) => {
                return throwError(() => err);
            })
        );
    }

    renameSystem(systemId: number, newSystemName: string): Observable<any> {
        return this.http
            .post(`${this.apiUrl}${PQM}/systemSetup/renameSystem`, {
                systemId,
                newSystemName
            })
            .pipe(tap(() => this.store.updateSystemName(systemId, newSystemName)));
    }

    private getSystems(): Observable<System[]> {
        return this.http.get<System[]>(`${this.apiUrl}${PQM}/systemSetup/getSystems`);
    }

    getSystemDetails(systemId: number): Observable<SystemDetails> {
        const params = new HttpParams().set('systemId', systemId);
        return this.http.get<SystemDetails>(`${this.apiUrl}${PQM}/systemSetup/systemDetails/${systemId}`, {
            params
        });
    }
}
